import {
  challengeRepository,
  habitRepository,
  logRepository,
  vacationAllowanceRepository,
} from '../db/database';
import {
  DEFAULT_COEFFICIENT,
  DEFAULT_SUCCESS_SCORE,
  SCORE_BAR_MAX,
  type AutoSource,
  type Challenge,
  type CompletionRatio,
  type Habit,
  type HabitCategory,
  type HabitId,
  type HabitLog,
  type HabitKind,
  type IconName,
  type IsoDate,
  type ProgressState,
} from '../models/types';
import { syncAutomaticCleaningLog } from '../services/cleaning';
import { getOrCreateProgressState } from '../services/dailyRollover';
import { getDayCover } from '../services/dayCover';
import {
  calculateLiveScore,
  isDayStreakEligible,
  type LiveScore,
} from '../services/scoring';
import { addDays, todayIso } from '../utils/date';
import { renderCheckIcon, renderIcon } from '../ui/icons';
import type { RouteId } from '../ui/tabBar';

/**
 * Anasayfa — uygulamanın ana ekranı.
 *
 * Üstte Başarı Barı, altında bugünün özeti ve alışkanlık listesi yer
 * alır. Ekranın tüm sayıları CLAUDE.md §7'deki formüllerden gelir;
 * bu dosyada hesap YAPILMAZ, yalnızca `services/scoring.ts`
 * çağrılır — iş mantığı ile görsel katmanın karışmaması için.
 */

/** Alışkanlık satırlarında gösterilen geçmiş noktası sayısı. */
const HISTORY_DAY_COUNT = 10;

/** Parçalı alışkanlık seçicisindeki oranlar (§7.1). */
const RATIO_OPTIONS: CompletionRatio[] = [0, 0.5, 0.75, 1];

/* ==================================================================
   Veri yükleme
   ================================================================== */

/** Bir günün `habitId -> oran` eşlemesi. */
type RatioMap = Map<HabitId, CompletionRatio>;

interface HistoryDay {
  date: IsoDate;
  ratios: RatioMap;
}

interface HomeData {
  today: IsoDate;
  habits: Habit[];
  todayRatios: RatioMap;
  /** Eskiden yeniye sıralı son 10 gün. */
  history: HistoryDay[];
  progress: ProgressState;
  activeChallenge: Challenge | undefined;
  liveScore: LiveScore;
  /**
   * Bugünün durumu — streak rozetinin rengini belirler.
   * `'none'` = henüz güvencede değil, `'vacation'` / `'reward'` =
   * dondurulmuş (§7.6 / §7.7), `'earned'` = bugün doğal olarak
   * seriye sayılacak kadar alışkanlık yapıldı (§7.3).
   */
  todayStatus: 'none' | 'earned' | 'vacation' | 'reward';
}

function toRatioMap(logs: { habitId: HabitId; completionRatio: CompletionRatio }[]): RatioMap {
  return new Map(logs.map((log) => [log.habitId, log.completionRatio]));
}

/**
 * Ekranın ihtiyaç duyduğu her şeyi tek seferde okur.
 *
 * Son 10 günün kayıtları gün gün çekiliyor (tarih indeksi üzerinden).
 * Alışkanlık başına sorgu yapmak yerine gün başına sorgu yapmak
 * bilinçli bir tercih: alışkanlık sayısı zamanla artabilir, ama gün
 * sayısı her zaman sabit 10'dur.
 */
async function loadHomeData(): Promise<HomeData> {
  const today = todayIso();

  // §7.5 — "Temizlik" alışkanlığının bugünkü durumunu, okumadan ÖNCE
  // tazele. Kayıt türetilmiş bir değer olduğu için her açılışta
  // yeniden hesaplanması güvenli; böylece kullanıcı temizlik
  // sayfasında bir görevi bitirip Anasayfa'ya döndüğünde rozeti
  // güncel görür — gecenin kapanışını beklemesi gerekmez.
  await syncAutomaticCleaningLog(today);

  const historyDates = Array.from({ length: HISTORY_DAY_COUNT }, (_, index) =>
    // En eski gün solda olsun diye geriye doğru sayıp ters çeviriyoruz.
    addDays(today, -(HISTORY_DAY_COUNT - 1 - index)),
  );

  const [habits, progress, challenges, historyLogs, allowance] = await Promise.all([
    habitRepository.listActive(),
    getOrCreateProgressState(),
    challengeRepository.list(),
    Promise.all(historyDates.map((date) => logRepository.listByDate(date))),
    vacationAllowanceRepository.get(),
  ]);

  const history: HistoryDay[] = historyDates.map((date, index) => ({
    date,
    ratios: toRatioMap(historyLogs[index] ?? []),
  }));

  // Bugün, geçmiş listesinin son elemanıdır.
  const todayRatios = history[history.length - 1]?.ratios ?? new Map();

  const todayLogs = [...todayRatios].map(([habitId, completionRatio]) => ({
    id: `${habitId}:${today}`,
    habitId,
    date: today,
    completionRatio,
  }));

  const activeChallenge = challenges.find((challenge) => challenge.status === 'active');

  // Rozetin rengi için bugünün durumu. Önce güvence (tatil/ödül),
  // sonra doğal uygunluk — dondurulmuş bir gün zaten seriye sayılacağı
  // için ayrıca %50 hesabına bakmaya gerek yok.
  const cover = getDayCover(today, allowance, progress);
  const todayStatus =
    cover !== 'none'
      ? cover
      : isDayStreakEligible(todayLogs, habits, {
            date: today,
            isFrozenDay: false,
            challengeCompletedToday: activeChallenge?.completedDates.includes(today) ?? false,
          })
        ? 'earned'
        : 'none';

  return {
    today,
    habits,
    todayRatios,
    history,
    progress,
    activeChallenge,
    liveScore: calculateLiveScore(todayLogs, habits, today, progress.barValue),
    todayStatus,
  };
}

/* ==================================================================
   Türetilmiş değerler
   ================================================================== */

/**
 * Bir alışkanlık o gün "yapıldı" sayılıyor mu?
 * §7.3 gereği parçalı alışkanlıkta %50 ve üstü yapıldı sayılır.
 */
function isDone(ratios: RatioMap, habitId: HabitId): boolean {
  return (ratios.get(habitId) ?? 0) >= 0.5;
}

/** "Bugün X / Y" kartındaki tamamlanma sayısı. */
function countDoneToday(data: HomeData): number {
  return data.habits.filter((habit) => isDone(data.todayRatios, habit.id)).length;
}

/** Aktif challenge bugün tamamlandı mı? */
function isChallengeDoneToday(data: HomeData): boolean {
  return data.activeChallenge?.completedDates.includes(data.today) ?? false;
}

/* ==================================================================
   HTML parçaları
   ================================================================== */

/** Tarihi "11 Eylül 2026" biçiminde yazar. */
function formatTodayLabel(date: IsoDate): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Metni HTML'e gömmeden önce güvenli hâle getirir.
 *
 * Alışkanlık adları kullanıcı tarafından yazılıyor (Faz 9). İçinde
 * `<` gibi bir karakter geçerse sayfanın yapısını bozmasın diye
 * tarayıcının kendi kaçış mekanizmasını kullanıyoruz.
 */
function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

/** Başarı Barı kartı — ekranın kahramanı. */
function renderScoreCard(liveScore: LiveScore): string {
  const percent = (liveScore.barValue / SCORE_BAR_MAX) * 100;

  return `
    <section class="score-card">
      <p class="score-card__label">Başarı Barı</p>
      <p class="score-card__value">
        <span class="score-card__number" data-score-number>${liveScore.barValue}</span>
        <span class="score-card__max">/ ${SCORE_BAR_MAX}</span>
      </p>
      <div class="score-card__track" role="progressbar"
           aria-valuemin="0" aria-valuemax="${SCORE_BAR_MAX}"
           aria-valuenow="${liveScore.barValue}" aria-label="Başarı Barı">
        <div class="score-card__fill" data-score-fill style="width: ${percent}%"></div>
      </div>
      <div class="score-card__delta-slot" data-delta-slot></div>
    </section>
  `;
}

/**
 * Streak rozetinin durum etiketi — ekran okuyucular için.
 * Renk tek başına bilgi taşımamalı.
 */
const BADGE_LABELS: Record<HomeData['todayStatus'], string> = {
  none: 'bugün henüz seriye sayılmadı',
  earned: 'bugün seriye sayılacak',
  vacation: 'bugün tatil günü',
  reward: 'bugün ödülle alındı',
};

/**
 * Üstteki tarih + streak rozeti satırı.
 *
 * Rozetin rengi bugünün durumunu anlatır: gün güvenceye alınınca
 * yeşile (tatilde maviye) döner. Böylece kullanıcı Streak ekranını
 * açmadan, tek bakışta "bugünü kurtardım mı?" sorusunu cevaplar.
 */
function renderHeader(data: HomeData): string {
  return `
    <header class="home-header">
      <p class="home-header__date">${formatTodayLabel(data.today)}</p>
      <button type="button" class="streak-badge streak-badge--${data.todayStatus}"
              data-open-streak
              aria-label="Day Streak detayını aç — ${BADGE_LABELS[data.todayStatus]}">
        ${renderIcon('flame', { size: 16 })}
        <span class="streak-badge__count">${data.progress.currentStreak}</span>
      </button>
    </header>
  `;
}

/** "Bugün X/Y" ve "Challenge" özet kartları. */
function renderSummaryRow(data: HomeData): string {
  const challenge = data.activeChallenge;
  const challengeDone = isChallengeDoneToday(data);

  let challengeBody: string;
  if (!challenge) {
    challengeBody = '<p class="summary-card__muted">Aktif challenge yok</p>';
  } else if (challengeDone) {
    challengeBody = `
      <p class="summary-card__challenge summary-card__challenge--done">
        <span>${escapeHtml(challenge.title)}</span>
        ${renderCheckIcon(13, 'currentColor')}
      </p>
    `;
  } else {
    challengeBody = `
      <p class="summary-card__challenge summary-card__challenge--pending">
        ${escapeHtml(challenge.title)}
      </p>
    `;
  }

  return `
    <div class="summary-row">
      <section class="summary-card">
        <p class="summary-card__label">Bugün</p>
        <p class="summary-card__value" data-done-count>
          ${countDoneToday(data)} / ${data.habits.length}
        </p>
      </section>
      <section class="summary-card">
        <p class="summary-card__label">Challenge</p>
        ${challengeBody}
      </section>
    </div>
  `;
}

/**
 * Bir alışkanlığın son 10 gününü nokta dizisi olarak çizer.
 *
 * Üç durum var:
 * - yeşil : o gün yapıldı
 * - kırmızı: o gün yapılmadı
 * - boş   : alışkanlık o tarihte henüz YOKTU ya da gün daha bitmedi
 *           (bugün). Sonradan eklenen bir alışkanlığın geçmişi
 *           kırmızıyla dolu görünmemeli — o günler gerçekten
 *           kaçırılmış değil.
 */
function renderHistoryDots(habit: Habit, history: HistoryDay[], today: IsoDate): string {
  const dots = history.map((day) => {
    if (habit.createdAt > day.date) {
      return '<span class="history-dot history-dot--absent"></span>';
    }
    if (isDone(day.ratios, habit.id)) {
      return '<span class="history-dot history-dot--done"></span>';
    }
    // Bugün henüz bitmedi: "kaçırıldı" demek için erken.
    if (day.date === today) {
      return '<span class="history-dot history-dot--pending"></span>';
    }
    return '<span class="history-dot history-dot--missed"></span>';
  });

  return `<div class="history-dots" aria-hidden="true">${dots.join('')}</div>`;
}

/** Boolean alışkanlığın yuvarlak işaretleme düğmesi. */
function renderBooleanControl(habit: Habit, done: boolean): string {
  return `
    <button type="button"
            class="habit-toggle${done ? ' habit-toggle--done' : ''}"
            data-habit-toggle="${habit.id}"
            role="switch" aria-checked="${done}"
            aria-label="${escapeHtml(habit.title)}">
      ${done ? renderCheckIcon(15) : ''}
    </button>
  `;
}

/**
 * Parçalı alışkanlığın 0 / 50 / 75 / 100 seçicisi.
 *
 * `current` değeri `undefined` ise kullanıcı o gün için HENÜZ bir
 * seçim yapmamıştır ve hiçbir seçenek vurgulanmaz. Bu ayrım önemli:
 * "kayıt yok" ile "bilerek 0 seçildi" aynı şey değildir — günün
 * başında her parçalı alışkanlığı kırmızı "0" ile göstermek, henüz
 * olmamış bir başarısızlığı duyurmak olurdu.
 */
function renderRatioControl(habit: Habit, current: CompletionRatio | undefined): string {
  const options = RATIO_OPTIONS.map((ratio) => {
    const isSelected = current !== undefined && ratio === current;
    // "0" seçili olduğunda kırmızı, diğerleri turuncu görünür —
    // sıfır bir başarı değil, "o gün yapılmadı" demek.
    const modifier = isSelected ? (ratio === 0 ? ' ratio-option--zero' : ' ratio-option--on') : '';

    return `
      <button type="button" class="ratio-option${modifier}"
              data-habit-ratio="${habit.id}" data-ratio="${ratio}"
              aria-pressed="${isSelected}">${ratio * 100}</button>
    `;
  });

  return `<div class="ratio-picker" role="group"
               aria-label="${escapeHtml(habit.title)} tamamlanma oranı">${options.join('')}</div>`;
}

/**
 * Otomatik ("Temizlik") alışkanlığın rozeti — tıklanamaz.
 *
 * Durumu kullanıcı değil sistem belirler (§7.5), bu yüzden bir düğme
 * değil sadece bir göstergedir.
 */
function renderAutoControl(done: boolean): string {
  return `
    <span class="auto-badge${done ? ' auto-badge--done' : ''}"
          title="Temizlik durumu otomatik hesaplanır">
      ${renderCheckIcon(14, 'currentColor', 3.6)}
    </span>
  `;
}

/** Alışkanlığın türüne göre doğru kontrolü seçer. */
function renderHabitControl(habit: Habit, ratios: RatioMap): string {
  // Kaydın hiç olmaması ile 0 olmasını ayırt edebilmek için `??  0`
  // yapmıyoruz — parçalı seçici bu farkı gösteriyor.
  const ratio = ratios.get(habit.id);
  const done = (ratio ?? 0) >= 0.5;

  const controls: Record<HabitKind, () => string> = {
    boolean: () => renderBooleanControl(habit, done),
    partial: () => renderRatioControl(habit, ratio),
    auto: () => renderAutoControl(done),
  };

  return controls[habit.kind]();
}

/** Tek bir alışkanlık satırı (kart). */
function renderHabitRow(habit: Habit, data: HomeData): string {
  return `
    <li class="habit-row" data-habit-row="${habit.id}">
      <span class="habit-row__icon">${renderIcon(habit.icon, { size: 19 })}</span>
      <div class="habit-row__body">
        <p class="habit-row__name">${escapeHtml(habit.title)}</p>
        ${renderHistoryDots(habit, data.history, data.today)}
      </div>
      ${renderHabitControl(habit, data.todayRatios)}
    </li>
  `;
}

/** Hiç alışkanlık yokken gösterilen boş durum. */
function renderEmptyState(): string {
  return `
    <div class="placeholder">
      <p class="placeholder__title">Henüz alışkanlık yok</p>
      <p class="placeholder__hint">
        Profil &gt; Alışkanlıkları Düzenle'den ilk alışkanlığını ekle.
      </p>
    </div>
  `;
}

/* ==================================================================
   Etkileşim — canlı puan güncellemesi (§7.2)
   ================================================================== */

/**
 * Başarı Barı'nı yeni değere taşır ve değişimi baloncuk olarak gösterir.
 *
 * Bar genişliği CSS geçişiyle yumuşak şekilde akar; sayı ise anında
 * güncellenir. Baloncuk (`floatUp`) animasyonu bitince kendini siler —
 * üst üste hızlı işaretlemelerde ekranda birikmesin diye her seferinde
 * öncekiler temizlenir.
 */
function updateScoreUi(container: HTMLElement, liveScore: LiveScore, delta: number): void {
  const numberElement = container.querySelector<HTMLElement>('[data-score-number]');
  const fillElement = container.querySelector<HTMLElement>('[data-score-fill]');
  const track = container.querySelector<HTMLElement>('.score-card__track');
  const deltaSlot = container.querySelector<HTMLElement>('[data-delta-slot]');

  if (numberElement) numberElement.textContent = String(liveScore.barValue);
  if (fillElement) {
    fillElement.style.width = `${(liveScore.barValue / SCORE_BAR_MAX) * 100}%`;
  }
  track?.setAttribute('aria-valuenow', String(liveScore.barValue));

  // Bar zaten tavana/tabana dayanmışsa değişim 0 olabilir; sıfır
  // baloncuğu göstermek kafa karıştırır.
  if (!deltaSlot || delta === 0) return;

  deltaSlot.innerHTML = '';
  const bubble = document.createElement('span');
  bubble.className = `score-delta${delta > 0 ? ' score-delta--gain' : ' score-delta--loss'}`;
  bubble.textContent = `${delta > 0 ? '+' : '−'}${Math.abs(delta)}`;
  bubble.addEventListener('animationend', () => bubble.remove());
  deltaSlot.appendChild(bubble);
}

/** "Bugün X / Y" sayacını tazeler. */
function updateDoneCount(container: HTMLElement, data: HomeData): void {
  const element = container.querySelector<HTMLElement>('[data-done-count]');
  if (element) {
    element.textContent = `${countDoneToday(data)} / ${data.habits.length}`;
  }
}

/**
 * Bir alışkanlığın satırını (kontrolü ve bugünün geçmiş noktasını)
 * yeniden çizer.
 *
 * Tüm sayfayı yeniden çizmiyoruz: o durumda Başarı Barı'nın dolum
 * animasyonu ve işaretleme sıçraması baştan başlar, yani kullanıcı
 * tam da geri bildirim beklerken ekran "zıplar".
 */
function refreshHabitRow(container: HTMLElement, habit: Habit, data: HomeData): void {
  const row = container.querySelector<HTMLElement>(`[data-habit-row="${habit.id}"]`);
  if (!row) return;

  const control = row.querySelector('.habit-toggle, .ratio-picker, .auto-badge');
  if (control) {
    // Yenisini eskisinin yanına koyup eskisini siliyoruz — böylece
    // kontrol satırdaki yerini (en sağ) koruyor.
    control.insertAdjacentHTML('afterend', renderHabitControl(habit, data.todayRatios));
    control.remove();
  }

  const dots = row.querySelector('.history-dots');
  if (dots) {
    dots.outerHTML = renderHistoryDots(habit, data.history, data.today);
  }
}

/** İşaretleme anındaki küçük "sıçrama" animasyonunu tetikler. */
function playBounce(container: HTMLElement, habitId: HabitId): void {
  const toggle = container.querySelector<HTMLElement>(`[data-habit-toggle="${habitId}"]`);
  if (!toggle) return;

  toggle.classList.remove('habit-toggle--bounce');
  // Sınıfı hemen geri eklersek tarayıcı animasyonu yeniden başlatmaz;
  // bu satır düzeni (layout) okuyarak tarayıcıyı "ara durumu işle"
  // diye zorlar ve animasyon her seferinde baştan oynar.
  void toggle.offsetWidth;
  toggle.classList.add('habit-toggle--bounce');
}

/**
 * Bir alışkanlığın bugünkü oranını kaydeder ve ekranı tazeler.
 *
 * Akış: kaydı yaz → yeni canlı puanı hesapla → farkı baloncukla
 * göster. Fark, barın ESKİ ve YENİ değeri arasındaki mesafedir; bu
 * yüzden bar tavana dayanmışsa baloncuk da doğru şekilde küçülür.
 */
async function setHabitRatio(
  container: HTMLElement,
  data: HomeData,
  habit: Habit,
  ratio: CompletionRatio,
): Promise<void> {
  await logRepository.upsert({
    id: `${habit.id}:${data.today}`,
    habitId: habit.id,
    date: data.today,
    completionRatio: ratio,
  });

  // Bellekteki kopyayı da güncelliyoruz ki ekran yeniden okumadan
  // doğru durumu gösterebilsin.
  data.todayRatios.set(habit.id, ratio);

  const todayLogs = [...data.todayRatios].map(([habitId, completionRatio]) => ({
    id: `${habitId}:${data.today}`,
    habitId,
    date: data.today,
    completionRatio,
  }));

  const previousBarValue = data.liveScore.barValue;
  data.liveScore = calculateLiveScore(
    todayLogs,
    data.habits,
    data.today,
    data.progress.barValue,
  );

  updateScoreUi(container, data.liveScore, data.liveScore.barValue - previousBarValue);
  updateDoneCount(container, data);
  updateStreakBadge(container, data, todayLogs);
  refreshHabitRow(container, habit, data);
  playBounce(container, habit.id);
}

/**
 * Streak rozetinin rengini tazeler.
 *
 * Sayfanın tamamı yeniden çizilmediği için (bkz. `refreshHabitRow`),
 * rozetin de elle güncellenmesi gerekiyor. Kullanıcı o anda eşiği
 * geçtiğinde rozet hemen yeşile dönsün diye: "bugünü kurtardım mı?"
 * sorusunun cevabı anında görünmeli.
 *
 * Dondurulmuş gün (tatil/ödül) burada yeniden hesaplanmaz — o durum
 * işaretleme ile değişmez, sayfa açılışında belirlenir.
 */
function updateStreakBadge(
  container: HTMLElement,
  data: HomeData,
  todayLogs: HabitLog[],
): void {
  if (data.todayStatus === 'vacation' || data.todayStatus === 'reward') return;

  const eligible = isDayStreakEligible(todayLogs, data.habits, {
    date: data.today,
    isFrozenDay: false,
    challengeCompletedToday: isChallengeDoneToday(data),
  });
  data.todayStatus = eligible ? 'earned' : 'none';

  const badge = container.querySelector<HTMLElement>('.streak-badge');
  if (!badge) return;
  badge.classList.toggle('streak-badge--earned', eligible);
  badge.classList.toggle('streak-badge--none', !eligible);
  badge.setAttribute(
    'aria-label',
    `Day Streak detayını aç — ${BADGE_LABELS[data.todayStatus]}`,
  );
}

/** Tıklama olaylarını bağlar. */
function attachHandlers(
  container: HTMLElement,
  data: HomeData,
  navigate: (route: RouteId) => Promise<void>,
): void {
  const habitsById = new Map(data.habits.map((habit) => [habit.id, habit]));

  container.querySelector('[data-open-streak]')?.addEventListener('click', () => {
    void navigate('streak');
  });

  // Tek bir dinleyiciyle tüm satırları yönetiyoruz: satırlar
  // işaretleme sonrası yeniden çizildiği için, her düğmeye ayrı
  // dinleyici bağlamak onları her seferinde yeniden bağlamayı
  // gerektirirdi.
  container.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest<HTMLElement>('[data-habit-toggle]');
    if (toggle) {
      const habit = habitsById.get(toggle.dataset['habitToggle'] ?? '');
      if (!habit) return;
      const isCurrentlyDone = isDone(data.todayRatios, habit.id);
      void setHabitRatio(container, data, habit, isCurrentlyDone ? 0 : 1);
      return;
    }

    const ratioButton = target.closest<HTMLElement>('[data-habit-ratio]');
    if (ratioButton) {
      const habit = habitsById.get(ratioButton.dataset['habitRatio'] ?? '');
      if (!habit) return;
      const ratio = Number(ratioButton.dataset['ratio']) as CompletionRatio;
      void setHabitRatio(container, data, habit, ratio);
    }
  });
}

/* ==================================================================
   Başlangıç verisi
   ================================================================== */

interface SeedDefinition {
  title: string;
  icon: IconName;
  kind: HabitKind;
  category: HabitCategory;
  /** Yalnızca otomatik alışkanlıklarda: durumunu hesaplayan kural. */
  autoSource?: AutoSource;
}

/**
 * İlk açılışta eklenen örnek alışkanlıklar.
 *
 * GEÇİCİ: Faz 9'da alışkanlık ekleme/düzenleme ekranı gelince
 * kullanıcı bunları değiştirebilecek. Şimdilik uygulamanın boş
 * açılmaması ve üç alışkanlık türünün de (boolean / parçalı /
 * otomatik) denenebilmesi için duruyor.
 */
const SEED_HABITS: SeedDefinition[] = [
  { title: 'Su iç', icon: 'water', kind: 'partial', category: 'nutrition' },
  { title: 'Erken uyu', icon: 'moon', kind: 'boolean', category: 'sleep' },
  { title: 'Yürüyüş', icon: 'walk', kind: 'partial', category: 'exercise' },
  { title: 'Sigarasız gün', icon: 'smoke', kind: 'boolean', category: 'custom' },
  { title: 'Kitap oku', icon: 'book', kind: 'boolean', category: 'reading' },
  { title: 'Temizlik', icon: 'broom', kind: 'auto', category: 'custom', autoSource: 'cleaning' },
  { title: 'Spor', icon: 'dumbbell', kind: 'boolean', category: 'exercise' },
  { title: 'Sağlıklı beslenme', icon: 'apple', kind: 'boolean', category: 'nutrition' },
  { title: 'Diş fırçalama', icon: 'toothbrush', kind: 'boolean', category: 'custom' },
  { title: 'Projeye çalış', icon: 'laptop', kind: 'boolean', category: 'custom' },
  { title: 'Az sosyal medya', icon: 'social', kind: 'boolean', category: 'screenTime' },
  { title: 'Şekersiz gün', icon: 'cube', kind: 'boolean', category: 'nutrition' },
];

/** Hiç alışkanlık yoksa örnekleri ekler. */
export async function seedHabitsIfEmpty(): Promise<void> {
  const existing = await habitRepository.list();
  if (existing.length > 0) return;

  const createdAt = todayIso();
  for (const [index, definition] of SEED_HABITS.entries()) {
    await habitRepository.upsert({
      id: crypto.randomUUID(),
      title: definition.title,
      icon: definition.icon,
      category: definition.category,
      kind: definition.kind,
      // exactOptionalPropertyTypes açık: `undefined` atamak yerine
      // alanı hiç eklemiyoruz.
      ...(definition.autoSource === undefined ? {} : { autoSource: definition.autoSource }),
      successScore: DEFAULT_SUCCESS_SCORE,
      coefficient: DEFAULT_COEFFICIENT,
      sortOrder: index,
      createdAt,
      archived: false,
    });
  }
}

/* ==================================================================
   Giriş noktası
   ================================================================== */

export async function renderHomePage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const data = await loadHomeData();

  const habitList = data.habits.length
    ? `<ul class="habit-list" role="list">
         ${data.habits.map((habit) => renderHabitRow(habit, data)).join('')}
       </ul>`
    : renderEmptyState();

  container.innerHTML = `
    ${renderHeader(data)}
    ${renderScoreCard(data.liveScore)}
    ${renderSummaryRow(data)}
    <h2 class="section-title">Alışkanlıklar</h2>
    ${habitList}
  `;

  attachHandlers(container, data, navigate);
}
